import { get, isArray, isEmpty, isObject, isString } from 'radash';
import { useCallback } from 'react';

import { LOCAL_STORAGE } from '@/constants';
import { useTranslation } from '@/hooks/useTranslation';
import { DataModelAttribute, DataModelAttributeOption } from '@/types/DataModel';

const useOptionTranslator = () => {

    const { t } = useTranslation();

    /**
     * There are many ways which an attribute option could be translated with, and it depends on the attribute definition:
     *
     * 1- An option could be translated under the following keys:
     *     - `option.OPTION_KEY`.
     *     - `attribute.ATTRIBUTE_NAME.option.OPTION_KEY`.
     *     - `optionList.OPTION_LIST_NAME.option.OPTION_KEY`.
     *
     * 2- An option could already have the translations attached to it in the attribute definition, via:
     *     - The available list of options { key, value }[] where value is the default translation that should be used.
     *     - A translation map attached to the option under the properties `label` or `labels`.
     *
     * The order of figuring out a translation is as follows:
     *     - Translation map.
     *     - option.OPTION_KEY.
     *     - Using the attribute name.
     *     - Using option lists.
     *     - And lastly we fallback to the `value` of the option.
     */
    const translateOption = useCallback((
        option: string,
        attribute: DataModelAttribute,
        loadedOptions?: DataModelAttributeOption[],
        defaultValue?: string): string => {

        const lookupLanguage = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY);

        // Case: translation maps and service lists
        if (loadedOptions) {

            const foundOption = loadedOptions.find(o => o.key === option || o.name === option);
            if (foundOption) {

                if (foundOption.translatedOption) {
                    return foundOption.translatedOption;
                }
                const translationsMap = get(foundOption, 'labels') as Record<string, string>;
                if (!isEmpty(translationsMap)) {

                    return getTranslatedOption(translationsMap, lookupLanguage);
                }

                const label = get(foundOption, 'label') as string;
                if(!isEmpty(label)) {
                    return label;
                }

                // Check if value is an object with labels property
                const objValue = get(foundOption, 'value');
                if(isObject(objValue) && !isArray(objValue)) {
                    const valueWithLabels = objValue as { labels?: Record<string, string> };
                    if (valueWithLabels.labels) {
                        if (isObject(valueWithLabels.labels) && !isArray(valueWithLabels.labels)) {
                            const labelsRecord = valueWithLabels.labels as Record<string, string>;
                            const translatedLabel = getTranslatedOption(labelsRecord, lookupLanguage);
                            if (translatedLabel) {
                                return translatedLabel;
                            }
                        }
                    }
                }

                return getDefaultTranslation(foundOption);
            }
        }

        // Case: option.OPTION_KEY
        let translationKey = `option.${option}`;
        let translation = t(translationKey);
        if (isTranslationFound(translation, translationKey)) {
            return translation;
        }

        // Case: Attribute name
        translationKey = `attribute.${attribute.name}.option.${option}`;
        translation = t(translationKey);
        if (isTranslationFound(translation, translationKey)) {
            return translation;
        }

        // Case: Option list
        if (isString(attribute.params?.keys)) {
            translationKey = `optionList.${attribute.params?.keys}.option.${option}`;
            translation = t(translationKey);

            if (isTranslationFound(translation, translationKey)) {
                return translation;
            }

        } else if (isArray(attribute.params?.values)) {
            const foundOption = attribute.params?.values.find(o => isObject(o) && o.key === option );
            if (!isEmpty(foundOption) && isObject(foundOption)) {
                // Edge case: value can be a structured object `{ label, labels }` in some data models.
                if (isObject(foundOption.value) && !isArray(foundOption.value)) {
                    const valueWithLabels = foundOption.value as { labels?: Record<string, string>; label?: string };
                    if (valueWithLabels.labels && isObject(valueWithLabels.labels) && !isArray(valueWithLabels.labels)) {
                        const translatedLabel = getTranslatedOption(valueWithLabels.labels, lookupLanguage);
                        if (translatedLabel) {
                            return translatedLabel;
                        }
                    }
                    if (isString(valueWithLabels.label) && !isEmpty(valueWithLabels.label)) {
                        return valueWithLabels.label;
                    }
                    return foundOption.key;
                }
                return foundOption.value;
            }

        }

        if (defaultValue) {
            return defaultValue;
        }

        return option;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTranslatedOption = (translationsMap: { [key: string]: string }, lookupLanguage: string | null) => {
        let translatedOption: string = get(translationsMap, lookupLanguage || 'en');

        if (!translatedOption && lookupLanguage?.includes('_')) {
            const primaryLanguage = lookupLanguage.split('_')[0];
            translatedOption = get(translationsMap, primaryLanguage);
        }

        return translatedOption;
    };

    const getDefaultTranslation = (option : { key: string, value: string }) => {
        return option.value || option.key;
    };

    const isTranslationFound = (translation: string, translationKey: string) => {
        return translation !== translationKey;
    };

    return { translateOption };

};

export { useOptionTranslator };
